/**
 * 蜜糖黄 · 阳光黄页 独立导航栏
 *
 * 设计理念：黄页目录 + 高识别琥珀黄
 * - 桌面端（lg+）：炭黑固定左侧竖栏（w-60），内容区右移，全仓唯一侧栏导航
 * - 移动端：顶部炭黑横条 + 左滑抽屉，内容与桌面一致
 * - 主 CTA 一律琥珀黄底 + 炭黑字（保证对比度，禁止黄底白字）
 */

import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function HoneyNavbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [logo, setLogo] = useState("");
  const [logoError, setLogoError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoggedIn, isAdmin, logout } = useAuth();
  const { siteName } = useSite();
  const { siteConfig, navMenus, navCategories } = useSiteConfig();
  const enableVip = siteConfig?.enable_vip ?? true;

  useEffect(() => {
    if (!siteConfig) return;
    if (siteConfig.logo) {
      setLogo(siteConfig.logo);
      setLogoError(false);
    }
  }, [siteConfig]);

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
    navigate("/");
  };

  const resolveHref = (menu: any) =>
    menu.type === "category"
      ? `/category/${menu.target_url || ""}`
      : menu.type === "page"
        ? `/page/${menu.target_url || ""}`
        : menu.type === "link"
          ? menu.target_url || "#"
          : "#";

  const isActive = (path: string) => location.pathname === path;

  const brandMark = (
    <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl overflow-hidden bg-[#F0A500] text-[#23201A]">
      {logo && !logoError ? (
        <img src={logo} alt={siteName} className="w-full h-full object-cover" onError={() => setLogoError(true)} />
      ) : (
        <span className="text-base font-black tracking-tight">
          {(siteName || "S").charAt(0).toUpperCase()}
        </span>
      )}
    </span>
  );

  const closeAll = () => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  };

  return (
    <>
      {/* ═══════════ 桌面端：左侧固定导航（lg+） ═══════════ */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 z-50 w-60 flex-col bg-[#23201A] text-[#FFF9EC] shadow-2xl shadow-black/20">
        {/* 顶部品牌区 */}
        <div className="px-5 pt-7 pb-5 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3 group" onClick={closeAll}>
            {brandMark}
            {siteName && (
              <span className="min-w-0">
                <span className="block text-[15px] font-bold tracking-tight text-[#FFF9EC] truncate group-hover:text-[#F0A500] transition-colors">
                  {siteName}
                </span>
                <span className="block text-[10px] text-[#8B8577] tracking-[0.25em] mt-0.5">CLASSIFIED</span>
              </span>
            )}
          </Link>
        </div>

        {/* 中部导航 */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <p className="px-3 pb-1 text-[10px] font-bold tracking-[0.2em] text-[#8B8577]">信息导航</p>

          {/* 首页 */}
          <Link
            to="/"
            onClick={closeAll}
            className={`flex items-center gap-2.5 px-3 py-2.5 mb-1 rounded-lg text-[13px] transition-colors ${
              isActive("/")
                ? "bg-[#F0A500] text-[#23201A] font-bold shadow-lg shadow-[#F0A500]/20"
                : "text-[#D8D1BF] hover:bg-white/5 hover:text-[#F0A500]"
            }`}
          >
            <i className="ri-home-4-line text-base" />
            首页
          </Link>

          {/* 后台导航菜单 */}
          {navMenus.map((menu) => {
            const href = resolveHref(menu);
            return (
              <Link
                key={menu.id}
                to={href}
                onClick={closeAll}
                className={`flex items-center gap-2.5 px-3 py-2.5 mb-1 rounded-lg text-[13px] transition-colors ${
                  href.startsWith("/") && isActive(href)
                    ? "bg-[#F0A500] text-[#23201A] font-bold shadow-lg shadow-[#F0A500]/20"
                    : "text-[#D8D1BF] hover:bg-white/5 hover:text-[#F0A500]"
                }`}
              >
                <i className="ri-menu-line text-base" />
                {menu.name}
              </Link>
            );
          })}

          {/* 分类快捷入口 */}
          {navCategories.length > 0 && (
            <>
              <p className="px-3 pt-4 pb-1 text-[10px] font-bold tracking-[0.2em] text-[#8B8577]">热门分类</p>
              {navCategories.slice(0, 8).map((cat) => {
                const href = `/category/${cat.slug}`;
                return (
                  <Link
                    key={cat.id}
                    to={href}
                    onClick={closeAll}
                    className={`flex items-center gap-2.5 px-3 py-2.5 mb-1 rounded-lg text-[13px] transition-colors ${
                      isActive(href)
                        ? "bg-[#F0A500] text-[#23201A] font-bold shadow-lg shadow-[#F0A500]/20"
                        : "text-[#D8D1BF] hover:bg-white/5 hover:text-[#F0A500]"
                    }`}
                  >
                    <i className={`${cat.icon || "ri-folder-line"} text-base`} />
                    {cat.name}
                  </Link>
                );
              })}
              <Link
                to="/category"
                onClick={closeAll}
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs text-[#F0A500] hover:bg-white/5 transition-colors"
              >
                <i className="ri-grid-fill text-base" />
                全部分类
              </Link>
            </>
          )}
        </nav>

        {/* 底部操作区 */}
        <div className="border-t border-white/10 p-4 space-y-3">
          <button
            onClick={() => navigate("/publish")}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#F0A500] text-[#23201A] text-sm font-bold hover:bg-[#FFB520] transition-colors shadow-lg shadow-[#F0A500]/20"
          >
            <i className="ri-add-circle-line text-base" />
            立即发布
          </button>

          {enableVip && (
            <Link
              to="/vip"
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[#F0A500]/40 text-[#F0A500] text-sm font-semibold hover:bg-[#F0A500]/10 transition-colors"
            >
              <i className="ri-vip-crown-line text-base" />
              开通 VIP
            </Link>
          )}

          {isLoggedIn ? (
            <div className="relative">
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="w-full flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-white/5 transition-colors cursor-pointer"
              >
                <span className="w-9 h-9 rounded-full bg-[#F0A500] text-[#23201A] flex items-center justify-center text-sm font-black overflow-hidden shrink-0">
                  {user?.avatar ? (
                    <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                  ) : (
                    (user?.nickname || "U").charAt(0).toUpperCase()
                  )}
                </span>
                <span className="min-w-0 flex-1 text-left">
                  <span className="block text-sm font-semibold text-[#FFF9EC] truncate">{user?.nickname || "我的账户"}</span>
                  <span className="block text-[10px] text-[#8B8577] mt-0.5">已登录</span>
                </span>
                <i className="ri-arrow-up-s-line text-[#8B8577] text-sm" />
              </button>

              {isUserMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setIsUserMenuOpen(false)} />
                  <div className="absolute left-[calc(100%+12px)] bottom-0 z-50 w-52 rounded-xl bg-[#FFF9EC] text-[#23201A] shadow-2xl border border-[#EAE1CB] p-1.5">
                    <Link to="/dashboard" onClick={closeAll}
                      className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-sm hover:bg-[#F0A500]/10 hover:text-[#B87A00] transition-colors">
                      <i className="ri-dashboard-3-line" />会员中心
                    </Link>
                    <Link to="/dashboard/my-listings" onClick={closeAll}
                      className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-sm hover:bg-[#F0A500]/10 hover:text-[#B87A00] transition-colors">
                      <i className="ri-file-list-3-line" />我的发布
                    </Link>
                    {enableVip && (
                      <Link to="/vip" onClick={closeAll}
                        className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-sm font-semibold text-[#B87A00] hover:bg-[#F0A500]/10 transition-colors">
                        <i className="ri-vip-crown-line" />开通 VIP
                      </Link>
                    )}
                    {isAdmin && (
                      <>
                        <div className="border-t border-[#EAE1CB] my-1" />
                        <Link to="/admin" onClick={closeAll}
                          className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-sm hover:bg-[#F0A500]/10 hover:text-[#B87A00] transition-colors">
                          <i className="ri-settings-4-line" />后台管理
                        </Link>
                      </>
                    )}
                    <div className="border-t border-[#EAE1CB] my-1" />
                    <button onClick={handleLogout}
                      className="w-full flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-sm text-[#8B8577] hover:bg-[#F0A500]/10 hover:text-[#B87A00] transition-colors text-left cursor-pointer">
                      <i className="ri-logout-box-r-line" />退出登录
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <Link to="/login"
                className="flex items-center justify-center gap-1 py-2.5 rounded-xl border border-white/15 text-[#FFF9EC] text-[13px] hover:border-[#F0A500] hover:text-[#F0A500] transition-colors">
                <i className="ri-user-line" />登录
              </Link>
              <Link to="/register"
                className="flex items-center justify-center gap-1 py-2.5 rounded-xl border border-white/15 text-[#FFF9EC] text-[13px] hover:border-[#F0A500] hover:text-[#F0A500] transition-colors">
                <i className="ri-user-add-line" />注册
              </Link>
            </div>
          )}
        </div>
      </aside>

      {/* ═══════════ 移动端：顶部横条 ═══════════ */}
      <header className="lg:hidden sticky top-0 z-50 bg-[#23201A] text-[#FFF9EC] shadow-lg">
        <div className="flex items-center justify-between h-14 px-4">
          <Link to="/" className="flex items-center gap-2.5 min-w-0" onClick={() => setIsMobileMenuOpen(false)}>
            <span className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg overflow-hidden bg-[#F0A500] text-[#23201A]">
              {logo && !logoError ? (
                <img src={logo} alt={siteName} className="w-full h-full object-cover" onError={() => setLogoError(true)} />
              ) : (
                <span className="text-sm font-black">{(siteName || "S").charAt(0).toUpperCase()}</span>
              )}
            </span>
            {siteName && <span className="text-[15px] font-bold truncate">{siteName}</span>}
          </Link>

          <div className="flex items-center gap-1.5">
            {!isLoggedIn && (
              <Link to="/login" className="p-2 rounded-lg text-[#D8D1BF] hover:text-[#F0A500] transition-colors" aria-label="登录">
                <i className="ri-user-line text-lg" />
              </Link>
            )}
            <button
              onClick={() => navigate("/publish")}
              className="flex items-center gap-1 px-3 py-2 rounded-lg bg-[#F0A500] text-[#23201A] text-xs font-bold hover:bg-[#FFB520] transition-colors"
            >
              <i className="ri-add-circle-line text-sm" />发布
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg text-[#FFF9EC] hover:text-[#F0A500] transition-colors"
              aria-label={isMobileMenuOpen ? "关闭菜单" : "打开菜单"}
            >
              <i className={`${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
            </button>
          </div>
        </div>

        {/* 移动端抽屉 */}
        {isMobileMenuOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/50" onClick={() => setIsMobileMenuOpen(false)} />
            <div className="absolute inset-y-0 left-0 w-[280px] bg-[#23201A] text-[#FFF9EC] flex flex-col shadow-2xl overflow-y-auto">
              <div className="px-5 pt-6 pb-4 border-b border-white/10 flex items-center justify-between">
                <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-2.5">
                  <span className="w-9 h-9 rounded-lg bg-[#F0A500] text-[#23201A] flex items-center justify-center font-black overflow-hidden">
                    {logo && !logoError ? (
                      <img src={logo} alt="" className="w-full h-full object-cover" onError={() => setLogoError(true)} />
                    ) : (
                      (siteName || "S").charAt(0).toUpperCase()
                    )}
                  </span>
                  {siteName && <span className="font-bold text-[15px]">{siteName}</span>}
                </Link>
                <button onClick={() => setIsMobileMenuOpen(false)} className="p-1.5 text-[#8B8577] hover:text-[#F0A500]" aria-label="关闭菜单">
                  <i className="ri-close-line text-lg" />
                </button>
              </div>

              <nav className="flex-1 px-3 py-4">
                <Link to="/" onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 mb-1 rounded-lg text-sm ${
                    isActive("/") ? "bg-[#F0A500] text-[#23201A] font-bold" : "text-[#D8D1BF] hover:bg-white/5"
                  }`}>
                  <i className="ri-home-4-line" />首页
                </Link>
                {navMenus.map((menu) => (
                  <Link key={menu.id} to={resolveHref(menu)} onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2.5 mb-1 rounded-lg text-sm text-[#D8D1BF] hover:bg-white/5 hover:text-[#F0A500] transition-colors">
                    <i className="ri-menu-line" />{menu.name}
                  </Link>
                ))}
                <p className="px-3 pt-4 pb-1 text-[10px] font-bold tracking-[0.2em] text-[#8B8577]">热门分类</p>
                {navCategories.slice(0, 8).map((cat) => (
                  <Link key={cat.id} to={`/category/${cat.slug}`} onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center gap-2.5 px-3 py-2.5 mb-1 rounded-lg text-sm ${
                      isActive(`/category/${cat.slug}`) ? "bg-[#F0A500] text-[#23201A] font-bold" : "text-[#D8D1BF] hover:bg-white/5"
                    }`}>
                    <i className={`${cat.icon || "ri-folder-line"}`} />{cat.name}
                  </Link>
                ))}
                <Link to="/category" onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs text-[#F0A500] hover:bg-white/5 transition-colors">
                  <i className="ri-grid-fill" />全部分类
                </Link>
              </nav>

              <div className="border-t border-white/10 p-4 space-y-2.5">
                {enableVip && (
                  <Link to="/vip" onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[#F0A500]/40 text-[#F0A500] text-sm font-semibold">
                    <i className="ri-vip-crown-line" />开通 VIP
                  </Link>
                )}
                {isLoggedIn ? (
                  <>
                    <Link to="/dashboard" onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-[#D8D1BF] hover:bg-white/5">
                      <i className="ri-dashboard-3-line" />会员中心
                    </Link>
                    <Link to="/dashboard/my-listings" onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-[#D8D1BF] hover:bg-white/5">
                      <i className="ri-file-list-3-line" />我的发布
                    </Link>
                    {isAdmin && (
                      <Link to="/admin" onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-[#D8D1BF] hover:bg-white/5">
                        <i className="ri-settings-4-line" />后台管理
                      </Link>
                    )}
                    <button onClick={handleLogout}
                      className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-[#8B8577] hover:bg-white/5 text-left">
                      <i className="ri-logout-box-r-line" />退出登录
                    </button>
                  </>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center justify-center gap-1 py-2.5 rounded-xl border border-white/15 text-[13px]">
                      <i className="ri-user-line" />登录
                    </Link>
                    <Link to="/register" onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center justify-center gap-1 py-2.5 rounded-xl border border-white/15 text-[13px]">
                      <i className="ri-user-add-line" />注册
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}
